import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import { Spring, animated } from 'react-spring/renderprops';
import { extent, group } from 'd3-array';
import { scaleLinear, scaleQuantile } from 'd3-scale';
import sortBy from 'lodash.sortby';

import mapToNest from '../mapToNest';
import exportSVG from '../exportSVG';

import backgroundChords from '../images/toggle-background-chords.svg';
// import info from '../images/info.svg';
import arrow from '../images/arrowdown.svg';
import close from '../images/close.svg';
import './styles.scss';

import { LanguageContext, localize } from '../Localization'

const showValue = true;
// const showPercent = true;
const showName = false;

const metrics = {
  defaultBarWidth: 24,
  vSpace: 24,
  nudgeX: 4,
};
const { defaultBarWidth, vSpace, nudgeX } = metrics;
let barWidth = defaultBarWidth;

const possibleColumns = ['School Site/Department', 'Resource / Grant', 'Activity', 'None'];
const columnNames = {
  'Resource / Grant-SchoolSiteBudgets': 'resource / grant',
  'School Site/Department-SchoolSiteBudgets': 'school site',
  'Activity-SchoolSiteBudgets': 'activity',
  'None-SchoolSiteBudgets': 'None',
  'Resource / Grant-CentralSupportsandAdmin': 'resource / grant',
  'School Site/Department-CentralSupportsandAdmin': 'department name',
  'Activity-CentralSupportsandAdmin': 'activity',
  'None-CentralSupportsandAdmin': 'None',
};

const languageColumnTextKeys = {
  "SchoolSiteDepartment": "school site",
  ResourceGrant: 'resource / grant',
  Activity: 'activity',
}

const colors = [
  '#1E3D51',
  '#2985C5',
  '#6A747A',
  '#8C7C6B',
  '#C56415',
].reverse();
const colorScale = scaleQuantile().range(colors);

const defaultFontRange = [10, 16];
const fontScale = scaleLinear().range(defaultFontRange).clamp(true);
const yScale = scaleLinear().range([0, 120]).clamp(true);

function downloadSVG(event) {
  return exportSVG(event.currentTarget);
}

export function generateKey(name) {
  return name.replace(/[^A-Za-z0-9]/g, '');
}

function generateChord(start, end) {
  const { color, value, activeTotal } = end;
  const startColor = start.color;
  const startName = start.name;
  const endName = end.name;
  const x0 = start.x,
    x1 = end.x,
    y0 = start.y,
    y1 = end.y,
    h0 = Math.max(1, start.height),
    h1 = Math.max(1, end.height),
    midX = x0 + ((x1 - x0) * 0.5);

  return {
    key: `${start.key}-${end.key}`,
    color,
    startColor,
    value,
    activeTotal,
    startName,
    endName,
    startX: start.x,
    endX: end.x,
    midX,
    midY: Math.min(y0, y1) + ((Math.max(y0 + h0, y1 + h1) - Math.min(y0, y1)) / 2),
    path: `
        M ${x0} ${y0}
        C ${midX} ${y0} ${midX} ${y1} ${x1} ${y1}
        L ${x1} ${y1 + h1}
        C ${midX} ${y1 + h1} ${midX} ${y0 + h0} ${x0} ${y0 + h0}
        Z
      `,
  };
}

let buildInHandle;
let hintHandle;

export default function Sankey(props) {
  const { width, data, id } = props;
  const scrollRef = useRef();
  const sankeyId = generateKey(id);

  const [columns, setColumns] = useState([]);
  const [drawers, setDrawers] = useState(possibleColumns.map(d => false));
  const [columnData, setColumnData] = useState([]);
  const [height, setHeight] = useState(width);
  const [chords, setChords] = useState([]);
  const [colorLegendData, setColorLegendData] = useState([]);
  const [selected, setSelected] = useState();
  const [instruction, setInstruction] = useState();
  const [tooltip, setTooltip] = useState();
  const [hintIndex, setHintIndex] = useState(0);
  const [displayedHints, setDisplayedHints] = useState([]);
  const [builtIn, setBuiltIn] = useState(false);

  const [hoverHighlight, setHoverHighlight] = useState();
  const [subselect, setSubselect] = useState();
  const [selectedDatum, setSelectedDatum] = useState();
  //
  // const [inlineEverything, setInlineEverything] = useState(false); // overrides others
  // const [hideEverything, setHideEverything] = useState(true);
  // const [filterBySelection, setFilterBySelection] = useState(true); // when false do hover thing tk
  const inlineEverything = false;
  const hideEverything = true;
  const filterBySelection = true;
  //
  // const [hideOtherCols, setHideOtherCols] = useState(false);
  //

  const language = useContext(LanguageContext)

  useEffect(() => {
    if (data.length && width) {

      //
      let narrow = false;
      let fontRange = defaultFontRange;
      barWidth = defaultBarWidth;
      if (window.innerWidth <= 1280) {
        barWidth = 16;
        fontRange = [8, 14];
        if (window.innerWidth <= 768) {
          // barWidth = 8;
          fontRange = [6, 10];
          narrow = true;
        }
      }
      //
      const { top } = scrollRef.current.getBoundingClientRect();
      if (top < 0) {
        scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      }

      const totalKey = 'Total Costs';

      const isTwoColumnDuplicate = columns.length === 1;
      const displayColumns = isTwoColumnDuplicate
        ? [columns[0], columns[0]]
        : columns.slice();

      let groupings = displayColumns
        .map((c, i) => {
          const leftIndex = i - 1;
          const rightIndex = i + 1;
          return {
            index: i,
            name: c,
            left: displayColumns[leftIndex],
            right: displayColumns[rightIndex],
          };
        })
        .map(c => {
          c.key = generateKey(c.name);
          c.data = sortBy(
            mapToNest(group(data, d => d[c.name]))
              .map(g => {
                g.name = g.key;
                g.key = generateKey(g.key);
                g.columns = [];
                if (c.left) {
                  g.align = 'end';
                  g.type = 'left';
                  const groups = mapToNest(group(g.values, d => d[c.left]));
                  // const colIncludesSubselect = subselect && groups.map(g => g.key).includes(subselect);
                  // console.log(colIncludesSubselect);
                  // console.log(g.key);
                  // console.log(groups.map(g => g.key));
                  // segment.key === selected || segment.childKeys.includes(selected) || (segment.childKeys.includes(subselect)
                  g.columns.push(
                    {
                      type: 'left',
                      align: 'end',
                      groups: sortBy(
                        // mapToNest(group(g.values, d => d[c.left]))
                        groups
                        .map(d => {
                          d.name = d.key;
                          d.key = generateKey(d.key);
                          d.parent = g.key;
                          d.parentKey = generateKey(c.left)

                          d.total = d.values.map(v => v[totalKey]).reduce((a, v) => a + v, 0);
                          d.activeTotal = inlineEverything ? d.total : d.values
                            // .filter(v => selected ? v.selectionKeys.includes(selected) : true)
                            // .filter(v => selected ? (d.key === subselect) : true)
                            // .filter(v => selected ? (v.selectionKeys.includes(selected) || d.key === subselect) : true)
                            .filter(v => (
                                selected
                                  ? filterBySelection
                                    ? v.selectionKeys.includes(selected)
                                    : d.key === selected || g.key === selected || (v.selectionKeys.includes(selected) && v.selectionKeys.includes(subselect))
                                 : true
                              ))
                            .map(v => v[totalKey])
                            .reduce((a, v) => a + v, 0);
                          const [students] = d.values.map(v => v['Students Served']);
                          d.students = +students.toLocaleString();
                          // d.perstudent = `$${(d.total / d.students).toLocaleString().split('.')[0]}`;
                          d.perstudent = `$${(d.activeTotal / d.students).toLocaleString().split('.')[0]}`;
                          return d;
                        })
                      // , 'total').reverse()
                      , 'activeTotal').reverse()
                    }
                  );
                }
                if (c.right) {
                  g.align = 'start';
                  g.type = 'right';
                  const groups = mapToNest(group(g.values, d => d[c.right]));
                  // const colIncludesSubselect = subselect && groups.map(g => g.key).includes(subselect);
                  // console.log(groups.map(g => g.key));
                  g.columns.push(
                    {
                      type: 'right',
                      align: 'start',
                      groups: sortBy(
                        // mapToNest(group(g.values, d => d[c.right]))
                        groups
                        .map(d => {
                          d.name = d.key;
                          d.key = generateKey(d.key);
                          d.parent = g.key;
                          d.parentKey = generateKey(c.right)

                          d.total = d.values.map(v => v[totalKey]).reduce((a, v) => a + v, 0);
                          d.activeTotal = inlineEverything ? d.total : d.values
                            // .filter(v => selected ? v.selectionKeys.includes(selected) : true)
                            // .filter(v => selected ? (d.key === subselect) : true)
                            // .filter(v => selected ? (v.selectionKeys.includes(selected) || d.key === subselect) : true)
                            .filter(v => (
                                selected
                                  ? filterBySelection
                                    ? v.selectionKeys.includes(selected)
                                    : d.key === selected || g.key === selected || (v.selectionKeys.includes(selected) && v.selectionKeys.includes(subselect))
                                 : true
                              ))
                            .map(v => v[totalKey])
                            .reduce((a, v) => a + v, 0);
                          const [students] = d.values.map(v => v['Students Served']);
                          d.students = +students.toLocaleString();
                          // d.perstudent = `$${(d.total / d.students).toLocaleString().split('.')[0]}`;
                          d.perstudent = `$${(d.activeTotal / d.students).toLocaleString().split('.')[0]}`;
                          return d;
                        })
                      // , 'total').reverse()
                      , 'activeTotal').reverse()
                    }
                  );
                }
                if (c.left && c.right) {
                  g.align = 'middle';
                  g.type = 'center';
                }
                g.total = g.columns[0].groups.map(d => d.total).reduce((a, v) => a + v, 0);
                // g.activeTotal = g.columns[0].groups.map(d => d.activeTotal).reduce((a, v) => a + v, 0);
                g.activeTotal = Math.max(
                  g.columns[0] ? g.columns[0].groups.map(d => d.activeTotal).reduce((a, v) => a + v, 0) : 0,
                  g.columns[1] ? g.columns[1].groups.map(d => d.activeTotal).reduce((a, v) => a + v, 0) : 0,
                );
                return g;
              })
          // , 'total').reverse();
          , 'activeTotal').reverse();
          return c;
        });

      const segmentTotals = sortBy(
        groupings
          .map(g => g.data.map(d => d.total))
          // .map(g => g.data.map(d => d.activeTotal).filter(v => v))
          .reduce((a, v) => a.concat(v), [])
        );
      // console.log(segmentTotals);
      colorScale.domain(segmentTotals);

      const yExtent = extent(groupings
        .map(g => g.data
          .map(d => d.columns.map(c => c.groups.map(v => v.total)).reduce((a, v) => a.concat(v), []))
          // .map(d => d.columns.map(c => c.groups.map(v => v.activeTotal)).reduce((a, v) => a.concat(v), []))
          .reduce((a, v) => a.concat(v), []))
        .reduce((a, v) => a.concat(v), []));
      yScale.domain([1, yExtent[1]]);

      fontScale
        .domain([1, yExtent[1]])
        .range(fontRange);

      groupings = groupings.map(c => {
        let cy = narrow
          ? vSpace * 2
          : vSpace;

        // const colIncludesSelected = selected && c.data.map(segment => segment.key).includes(selected);
        c.data = c.data.map(segment => {
          // segment.color = colorScale(segment.total);
          // segment.fontSize = fontScale(segment.total);
          segment.color = colorScale(segment.activeTotal);
          segment.legendColor = colorScale(segment.total);
          segment.fontSize = fontScale(segment.activeTotal);
          segment.y = cy;
          segment.childKeys = segment.columns
            .map(c => c.groups.map(group => group.key))
            .reduce((a, v) => a.concat(v), []);

          const y = [0, 0];
          segment.columns.map((c, i) => (c.groups.map(g => {
            // const barHeight = yScale(g.total);
            const barHeight = yScale(g.activeTotal);
            g.y = y[i];
            // const selectedBar = ;
            const selectedBar = inlineEverything
              ? true
              : g.activeTotal !== 0;
              // : !selected || [segment.key, g.key].includes(selected);

            g.barHeight = selectedBar ? barHeight : 0;
            // g.fontSize = fontScale(g.total);
            g.fontSize = fontScale(g.activeTotal);
            g.height = selectedBar ? Math.max(g.barHeight, g.fontSize) : 0;
            g.parentY = segment.y;
            g.color = segment.color;
            // g.value = `$${g.total.toLocaleString()}`;
            // g.percent = `${(g.total / segment.total * 100).toFixed(0)}%`;
            g.value = `$${g.activeTotal.toLocaleString()}`;
            g.percent = `${(g.activeTotal / segment.activeTotal * 100).toFixed(0)}%`;
            y[i] += g.height;
            return g;
          })));

          segment.height = Math.max(...y);
          cy += segment.height;
          // cy += (hideEverything || (!colIncludesSelected && hideOtherCols)) && segment.height === 0
          cy += hideEverything && segment.height === 0
            ? 0
            : narrow
              ? segment.fontSize * 4
              : segment.fontSize * 2;

          //
          // segment.colIncludesSelected = colIncludesSelected;

          segment.narrow = narrow;

          return segment;
        });
        return c;
      });

      const colorLegendData = mapToNest(group(
        groupings
          .map(g => g.data)
          .reduce((a, v) => a.concat(v), [])
        , d => d.legendColor));
      setColorLegendData(colorLegendData);

      // const centerChords = groupings[1]
      //   ? groupings[1].data.map(segment => {
      //       return segment.columns.map(column => {
      //         return column.groups.map(group => {
      //           return group;
      //         });
      //       }).reduce((a, v) => a.concat(v), []);
      //     }).reduce((a, v) => a.concat(v), [])
      //   : [];
      const centerLeftChords = groupings[1]
        ? groupings[1].data.map(segment => {
            return segment.columns[0]
              ? segment.columns[0].groups.map(group => {
                return group;
              })
              : [];
          }).reduce((a, v) => a.concat(v), [])
        : [];

      const centerRightChords = groupings[1]
        ? groupings[1].data.map(segment => {
            return segment.columns[1]
              ? segment.columns[1].groups.map(group => {
                return group;
              })
              : [];
          }).reduce((a, v) => a.concat(v), [])
        : [];

      const leftChordsWidth = groupings.length === 3
        ? width / 2 - barWidth
        : isTwoColumnDuplicate
          ? 0
          : width - barWidth;

      const leftChords = groupings[0]
        ? groupings[0].data.map(segment => {
            return segment.columns.map(column => {
              return column.groups.map(group => {
                return group;
              });
            }).reduce((a, v) => a.concat(v), []);
          }).reduce((a, v) => a.concat(v), [])
          .map(start => {
            // const end = centerChords.find(cc => cc.parent === start.key && cc.key === start.parent);
            const end = centerLeftChords.find(cc => cc.parent === start.key && cc.key === start.parent);
            return generateChord(
              {
                ...start,
                x: barWidth,
                y: start.y + start.parentY,
              },
              {
                ...end,
                x: leftChordsWidth,
                y: end.y + end.parentY,
              },
            );
          }).reverse()
        : [];

      const rightChords = groupings[2]
        ? groupings[2].data.map(segment => {
            return segment.columns.map(column => {
              return column.groups.map(group => {
                return group;
              });
            }).reduce((a, v) => a.concat(v), []);
          }).reduce((a, v) => a.concat(v), [])
          .map(start => {
            // const end = centerChords.find(cc => cc.parent === start.key && cc.key === start.parent);
            const end = centerRightChords.find(cc => cc.parent === start.key && cc.key === start.parent);
            return generateChord(
              {
                ...end,
                x: width / 2 + barWidth,
                y: end.y + end.parentY,
              },
              {
                ...start,
                x: width - barWidth,
                y: start.y + start.parentY,
              },
            );
          }).reverse()
        : [];

      setChords(leftChords.concat(rightChords)); // .filter(c => c.height !== 0)
      const height = groupings.length
        ? Math.max(...groupings.map(g => g.data.map(d => d.height + d.fontSize * (narrow ? 4 : 2)).reduce((a, v) => a + v, 0))) + (vSpace * 2) // + (barWidth * 1.5)
        : 0;
      setHeight(lastHeight => height === 0 || height > lastHeight ? height : lastHeight);
      setColumnData(groupings);

      const selectedDatum = selected
        ? groupings.map(g => g.data).reduce((a, v) => a.concat(v), []).find(d => d.key === selected)
        : null;

      if (selectedDatum) {
        let textAlign = 'left';
        let marginLeft = 0;
        let marginRight = 'auto'
        let translateX = 0;
        if (selectedDatum.type === 'center') {
          marginLeft = 'auto';
          textAlign = 'center';
          translateX = 100;
        } else if (selectedDatum.type === 'left') {
          marginRight = 0;
          marginLeft = 'auto';
          textAlign = 'right';
          translateX = 200;
        }

        selectedDatum.indicatorStyle = {
          width: width / 3,
          transform: `translateX(${translateX}%)`,
        };
        selectedDatum.filterStyle ={
          marginLeft,
          marginRight,
          textAlign,
        };
      }
      setSelectedDatum(selectedDatum);

      //
      clearTimeout(buildInHandle);
      if (!builtIn && !columns.length) {
        buildInHandle = setTimeout(() => {
          setColumns([possibleColumns[0]]);
          setHintIndex(1);
          setBuiltIn(true);
        }, 600);
      }
      //
    }
  }, [width, data, columns, selected, builtIn, inlineEverything, hideEverything, subselect, filterBySelection]);
  // }, [width, data, columns, selected, builtIn, inlineEverything, hideEverything, hideOtherCols]);

  const clearHint = useMemo(() => (
    function() {
      if (hintIndex) {
        setDisplayedHints(displayedHints => (
          displayedHints.includes(hintIndex)
            ? displayedHints
            : displayedHints.concat(hintIndex)
        ));
      }
      setHintIndex(0);
    }
  ), [hintIndex]);

  const clickColumn = useCallback((c, active, isEverything) => event => {
    event.preventDefault()
    event.stopPropagation()
    let nextColumns = columns.slice();
    if (active) {
      nextColumns = nextColumns.filter(d => d !== c);
    } else if (isEverything) {
      nextColumns = sortBy(possibleColumns, c => {
        const index = columns.indexOf(c);
        return index >= 0 ? index : possibleColumns.length;
      }).filter(p => p !== 'None');
    } else {
      nextColumns.push(c);
    }
    if ([0, 1, 2].includes(nextColumns.length)) {
      if (hintIndex) {
        setDisplayedHints(displayedHints => (
          displayedHints.includes(hintIndex)
            ? displayedHints
            : displayedHints.concat(hintIndex)
        ));
      }
      setHintIndex(nextColumns.length);
    } else {
      clearHint();
    }
    setColumns(nextColumns);
    setSelected(null);
    setSubselect(null);

  },[clearHint, columns, hintIndex])
  const columnToggles = useMemo(() => {
    const orderedColumns = sortBy(possibleColumns, c => {
      const index = columns.indexOf(c);
      return index >= 0 ? index : possibleColumns.length;
    });
    return (
      <div className='columnSelectors toggles' style={{ backgroundImage: `url(${backgroundChords})` }}>
        {possibleColumns
          .map((c, i) => {
            const order = orderedColumns.indexOf(c);
            const isEverything = c === 'None';
            const everythingSelected = !possibleColumns
              .filter(p => p !== 'None')
              .map(p => columns.includes(p))
              .includes(false);
            const active = columns.includes(c) || (isEverything && everythingSelected);
            const label = isEverything
              ? localize(language, 'visualization', 'everything title')
              : localize(language, 'visualization', `${columnNames[`${c}-${sankeyId}`]} title`);
            const copy = isEverything
              ? localize(language, 'visualization', 'everything description')
              : localize(language, 'visualization', `${columnNames[`${c}-${sankeyId}`]} description`);

            return (
              <div
                key={generateKey(c)}
                className='outerAdd'
                style={{ transform: `translateX(${-100 * i}%)` }}
              >
                <div
                  className='transformAdd'
                  style={{ transform: `translateX(${order * 100}%)` }}
                >
                  <div
                    className={`add${active ? ' active' : ''}`}
                    // ${isEverything ? ' everything' : ''}
                    onClick={clickColumn(c, active, isEverything)}
                  >
                    <div className='innerAdd'>
                      <div className='heading'>
                        <span>
                          {label}
                        </span>
                        <img src={arrow} alt='' />
                      </div>
                      <div className='content'>
                        <span>
                          {copy}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        }
      </div>
    );
  }, [columns, sankeyId, language, clickColumn]);

  const columnSelects = useMemo(() => {
    return (
      <div className='columnSelectors'>
        {columns.concat(possibleColumns).slice(0, 3).map((c, i) => {
          let style = { 'margin': '0 auto' };
          if (i === 0) {
            style = { marginRight: 'auto' };
          } else if (i === (columns.length - 1)) {
            style = { marginLeft: 'auto' };
          }

          let position = i;
          if (i >= columns.length) {
            position = i === 0
              ? -2
              : possibleColumns.length;
          } else if (columns.length > 2) {
            position = 0;
          }
          const columnStyle = {
            transform: `translateX(${position * 100}%)`,
            zIndex: 5 + i,
          };

          return (
            <div key={i} className='columnSelector' style={columnStyle}>
              <div className='selector' style={style}>
                <div
                  className='label'
                  onClick={() => {
                    setDrawers(drawers => {
                      const nextDrawers = drawers.slice();
                      nextDrawers[i] = !nextDrawers[i];
                      return nextDrawers;
                    });
                  }}
                >
                  <span>
                    {localize(language, 'visualization', `${columnNames[`${c}-${sankeyId}`]} title`)}
                  </span>
                  <img src={arrow} alt='' />
                </div>
                <div className={`drawer ${drawers[i] ? 'open' : 'closed'}`}>
                  {possibleColumns.map((d, j) => d === c ? null : (
                    <div
                      key={j}
                      className='option'
                      onClick={() => {
                        const isNone = d === 'None';
                        let nextColumns = columns.slice();
                        if (isNone) {
                          clearHint();
                          nextColumns.splice(i, 1);
                        } else {
                          nextColumns[i] = d;
                        }
                        setColumns(nextColumns);
                        setDrawers(drawers.map(d => false));
                        setSelected(null);
                        setSubselect(null);
                      }}
                    >
                      <span>
                        {columnNames[`${d}-${sankeyId}`]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [columns, drawers, sankeyId, clearHint, language]);

  const hint = useMemo(() => {
    const hintStates = [
      {
        copy: '',
        class: 'hidden',
        duration: null,
        next: null,
      },
      {
        copy: (
          <span>
            {
              language === 'english' ?
                "Let's start with School Site budgets. This is a simple break down of budgets for each school site. You can roll over or click on the diagram to see more detailed information. Next, let's see how school site is compared to another group. You can select another group above or you can select here " :
                "Comencemos con los presupuestos de las escuelas. Este es un desglose simple de los presupuestos para cada sitio escolar. Puede desplazarse o hacer clic en el diagrama para ver información más detallada. A continuación, veamos cómo se compara el sitio de la escuela con otro grupo. Puede seleccionar otro grupo arriba o puede seleccionar aquí "
            }
            <span onClick={clickColumn('Resource / Grant', false, false)} className='selectColumn'>
              {language === 'english' ? "Grant Names" : "Nombres de subvenciones"}
            </span>
            {language === 'english' ? ' or ' : ' o '}
            <span onClick={clickColumn('Activity', false, false)} className='selectColumn'>
              {language === 'english' ? 'Activities' : 'Actividades'}
            </span>
          </span>
        ),
        class: 'left',
        duration: null,
        // duration: 6000,
        next: 0,
      },
      {
        copy: localize(language, 'visualization', 'hint 2 left'),
        class: 'left',
        duration: 5000,
        next: 3,
      },
      {
        copy: localize(language, 'visualization', 'hint 3 right'),
        class: 'right',
        duration: 6000,
        next: 0,
      },
    ];
    const hintState = displayedHints.includes(hintIndex)
      ? hintStates[0]
      : hintStates[hintIndex];

    clearTimeout(hintHandle);
    if (hintState.duration) {
      hintHandle = setTimeout(() => {
        if (!displayedHints.includes(hintIndex)) {
          setDisplayedHints(displayedHints.concat(hintIndex));
        }
        setHintIndex(hintState.next);
      }, hintState.duration);
    }

    return (
      <div
        className={`hint ${hintState.class}`}
        onClick={clearHint}
      >
        <div className='close'>
          <img src={close} alt='x' />
        </div>
        <span key={hintIndex} className={`s-${hintIndex}`}>
          {hintState.copy}
        </span>
      </div>
    );
  }, [hintIndex, displayedHints, clearHint, language, clickColumn]);

  // const GradientAnimation = useMemo(() => (
  //   Keyframes.Spring(async next => {
  //     while (true) {
  //       await next({
  //         from: {
  //           offset: 15,
  //           opacity: 1,
  //         },
  //         to: {
  //           offset: 92.5,
  //           opacity: 0.25,
  //         },
  //       });
  //       await next({
  //         from: {
  //           offset: 92.5,
  //           opacity: 0.25,
  //         },
  //         to: {
  //           offset: 15,
  //           opacity: 1,
  //         },
  //       });
  //     }
  //   })
  // ), []);

  const chordGradients = useMemo(() => (
    chords.map((c, i) => (
      <linearGradient
        key={`${i}-${c.key}`}
        id={c.key}
      >
        <stop offset='0%' stopColor={c.startColor} />
        <stop offset='100%' stopColor={c.color} />
      </linearGradient>
    ))
  ), [chords]);

  const chordPaths = useMemo(() => {
    const tooltipNameKey = tooltip ? generateKey(tooltip.name) : null;
    const tooltipParentKey = tooltip ? generateKey(tooltip.parent) : null;
    return (
      chords.map((c, i) => {
        const splitKey = c.key.split('-');
        const hovered = (
            (tooltip && splitKey.includes(tooltipNameKey) && splitKey.includes(tooltipParentKey))
              ||
            (hoverHighlight && splitKey.includes(hoverHighlight))
          );
        let opacity = hovered
          ? 0.375
          : 0.05; // 0.1;
        if (selected) {
          opacity = 0; // 0.025;
          // if (splitKey.includes(selected)) {
          if ((filterBySelection && c.activeTotal && !inlineEverything) || splitKey.includes(selected) || (splitKey.includes(subselect) && c.activeTotal)) {
            opacity = hovered
              ? 0.375 //0.5
              : tooltip || hoverHighlight
                ? 0.05 // 125
                : 0.125; // 25;
          }
        }
        return opacity ? (
          <Spring
            native
            key={`${c.key}-${c.midX}`}
            from={{
              opacity: 0,
            }}
            to={{
              opacity,
              path: c.path,
            }}
          >
            {({ opacity, path }) => (
              <animated.path
                key={c.key}
                fill={`url('#${c.key}')`}
                fillOpacity={opacity}
                d={path}
                stroke='none'
              />
            )}
          </Spring>
        ) : null;
      })
    );
  }, [chords, selected, tooltip, filterBySelection, inlineEverything, subselect, hoverHighlight]);

  const columnGroups = useMemo(() => (
    columnData.map((column, i) => {
      const cx = i === 0 ? barWidth : width / (columnData.length - 1) * i;
      const isTwoColumnDuplicate = columns.length === 1 && i === 1 && column.key === columnData[0].key;
      const hideDuplicateLabels = columns.length === 1 && i === 0 && column.key === columnData[1].key;

      return isTwoColumnDuplicate ? null : (
        <g
          key={`${i}-${column.key}`}
          transform={`translate(${cx}, 0)`}
        >
          {column.data.map((segment, j) => {
            // const segmentSelected = (
            //   !selected || segment.key === selected || segment.childKeys.includes(selected)
            // );
            const segmentSelected = (
              (filterBySelection && segment.height && !inlineEverything)
                ||
              (!selected || segment.key === selected || segment.childKeys.includes(selected) || (segment.childKeys.includes(subselect) && segment.height))
            );

            const textOpacity = inlineEverything && !segmentSelected
              ? 0.125
              : segmentSelected
                ? 1
                // : hideEverything
                // : (hideEverything || (!segment.colIncludesSelected && hideOtherCols)) && segment.height === 0
                : (hideEverything && segment.height === 0) || !filterBySelection
                  ? 0
                  : 0.1875; // so sorry

            const rectOpacity = segmentSelected
              ? 0.25
              : !filterBySelection
                ? 0
                : 0.125;
            const barType = segment.key === selected ? 'close' : 'open';
            // const textType = !segmentSelected && barType === 'open' && !hideEverything ? 'hidden' : 'none';
            const textType = 'hoverhighlight';
            const noSelect = textOpacity === 0;

            const subselectType = rectOpacity && !filterBySelection && segment.key !== selected ? 'subselect' : 'none';
            return (
              <Spring
                native
                key={segment.key}
                from={{
                  transform: `translate(${-barWidth}, 0)`,
                  // height: 0,
                  textOpacity: 0,
                  rectOpacity: 0,
                }}
                to={{
                  transform: `translate(${-barWidth}, ${segment.y})`,
                  // height: segment.height,
                  textOpacity,
                  rectOpacity,
                  // rectOpacity: segmentSelected ? 0.25 : 0.125,
                }}
              >
                {({ transform, textOpacity, rectOpacity }) => (
                  <animated.g
                    key={segment.key}
                    transform={transform}
                    onClick={noSelect ? null : () => {
                      clearHint();
                      setInstruction(null);
                      setSubselect(null);
                      if (selected === segment.key) {
                        setSelected(null);
                      } else {
                        setSelected(segment.key);
                      }
                    }}
                    style={noSelect ? null : { cursor: 'pointer' }}
                  >
                    {segment.narrow ? (
                      <>
                        <animated.text
                          data-type={textType}
                          data-hoverhighlight={segment.key}
                          dy={-segment.fontSize * 2}
                          dx={segment.align === 'start' ? 0 : barWidth}
                          textAnchor={segment.align}
                          fontSize={segment.fontSize}
                          fill={segment.color}
                          fontWeight='bold'
                          fillOpacity={textOpacity}
                        >
                          {localize(language, languageColumnTextKeys[column.key], segment.name.toLowerCase())}
                        </animated.text>
                        <animated.text
                          dy={-segment.fontSize / 2}
                          dx={segment.align === 'start' ? 0 : barWidth}
                          textAnchor={segment.align}
                          fontSize={segment.fontSize}
                          fill={segment.color}
                          fontWeight='bold'
                          fillOpacity={textOpacity}
                        >
                          {/* ${segment.total.toLocaleString()} */}
                          ${segment.activeTotal.toLocaleString()}
                        </animated.text>
                      </>
                    ) : (
                      <animated.text
                        data-type={textType}
                        data-hoverhighlight={segment.key}
                        dy={-segment.fontSize / 2}
                        dx={segment.align === 'start' ? 0 : barWidth}
                        textAnchor={segment.align}
                        fontSize={segment.fontSize}
                        fill={segment.color}
                        fontWeight='bold'
                        fillOpacity={textOpacity}
                      >
                        {/* {segment.name} ${segment.total.toLocaleString()} */}
                        {localize(language, languageColumnTextKeys[column.key], segment.name.toLowerCase())} ${segment.activeTotal.toLocaleString()}
                      </animated.text>
                    )}
                    <animated.rect
                      width={segment.columns.length * barWidth}
                      // height={height}
                      height={segment.height}
                      fill={segment.color}
                      fillOpacity={rectOpacity}
                      data-type={subselectType}
                      //&& !inlineEverything
                      data-subselect={segment.key}
                    />
                    <g>
                      {segment.columns.map((c, k) => (
                        <g
                          key={k}
                          transform={`translate(${barWidth * k}, 0)`}
                        >
                          {c.groups.map((group, l) => {
                            const { value, percent, name } = group;
                            const tall = group.height >= group.fontSize * 2;

                            const showPercent = selected ? selected === group.parent : true;
                            let topLabel = '';
                            let bottomLabel = '';
                            if (showName && tall) {
                              bottomLabel = name;
                            }
                            if (showValue && showPercent) {
                              topLabel = `${value} (${percent})`
                            } else if (showValue) {
                              topLabel = value;
                            } else if (showPercent) {
                              topLabel = percent;
                            } else if (showName) {
                              topLabel = name;
                              bottomLabel = '';
                            }
                            if ((showValue || showPercent) && showName && !tall) {
                              topLabel += ` ${name}`;
                            }

                            // const opacity = [segment.key, group.key].includes(selected) || !selected
                            const subselectHover = (!filterBySelection && [segment.key, group.key].includes(subselect) && group.activeTotal);

                            const opacity = !selected || (filterBySelection && group.height && !inlineEverything) || [segment.key, group.key].includes(selected) || subselectHover
                              ? 1
                              : inlineEverything
                                ? 0.125
                                : 0;

                            return opacity ? (
                              <Spring
                                native
                                key={group.key}
                                from={{
                                  transform: `translate(0, 0)`,
                                  // height: 0,
                                  // opacity: 0,
                                }}
                                to={{
                                  transform: `translate(0, ${group.y})`,
                                  // height: Math.max(1, group.barHeight),
                                  // opacity,
                                }}
                              >
                                {({ transform,
                                  // opacity
                                }) => (
                                  <animated.g
                                    key={group.key}
                                    transform={transform}
                                    fillOpacity={opacity}
                                    // style={{ pointerEvents: subselectHover ? 'none' : 'auto' }}
                                  >
                                    <rect
                                      width={barWidth}
                                      // height={height}
                                      height={Math.max(1, group.barHeight)}
                                      fill={segment.color}
                                      stroke='transparent'
                                    />
                                    <line x2={barWidth} stroke='white' />
                                    {
                                      // segment.narrow ? (
                                      //   <rect
                                      //     width={barWidth}
                                      //     height={group.height}
                                      //     fill='transparent'
                                      //     fillOpacity='0'
                                      //     stroke='transparent'
                                      //   />
                                      // ) : (
                                      // subselectHover ? null : (
                                        <rect
                                          width={barWidth}
                                          height={group.height}
                                          fill='transparent'
                                          fillOpacity='0'
                                          stroke='transparent'
                                          data-type={barType}
                                          data-parent={localize(language, languageColumnTextKeys[column.key], segment.name.toLowerCase())}
                                          data-name={localize(language, languageColumnTextKeys[group.parentKey], group.name.toLowerCase())}
                                          data-value={group.value}
                                          data-perstudent={group.perstudent}
                                          data-students={group.students}
                                          data-color={segment.color}
                                          data-h={c.type}
                                          data-v={segment.y + group.y}
                                        />
                                      // )
                                    }
                                    {hideDuplicateLabels ? null : (
                                      <>
                                        <text
                                          key='topLabel'
                                          dx={c.align === 'start' ? barWidth + nudgeX : -nudgeX}
                                          dy={group.fontSize}
                                          fontSize={group.fontSize}
                                          textAnchor={c.align}
                                          // fill={group.total >= 0 ? segment.color : '#CC3300'}
                                          fill={group.activeTotal >= 0 ? segment.color : '#CC3300'}
                                        >
                                          {topLabel}
                                        </text>
                                        <text
                                          key='bottomLabel'
                                          dx={c.align === 'start' ? barWidth + nudgeX : -nudgeX}
                                          dy={group.fontSize * 2}
                                          fontSize={group.fontSize}
                                          textAnchor={c.align}
                                          // fill={group.total >= 0 ? segment.color : '#CC3300'}
                                          fill={group.activeTotal >= 0 ? segment.color : '#CC3300'}
                                        >
                                          {bottomLabel}
                                        </text>
                                      </>
                                    )}
                                  </animated.g>
                                )}
                              </Spring>
                            ) : null;
                          })}
                        </g>
                      ))}
                    </g>
                  </animated.g>
                )}
              </Spring>
            );
          })}
        </g>
      );
    })
  ), [columnData, columns.length, selected, width, clearHint, inlineEverything, hideEverything, filterBySelection, subselect, language]);
  // ), [columnData, columns.length, selected, width, clearHint, inlineEverything, hideEverything, hideOtherCols]);

  const colorLegend = useMemo(() => {
    const groupTotal = colorLegendData
      // .map(group => group.values.length)
      .map(group => group.values.filter(v => v.height).length)
      // .map(group => group.values.map(v => v.values.length).reduce((a, v) => a + v, 0))
      .reduce((a, v) => a + v, 0);

    const xScale = scaleLinear()
      .domain([0, groupTotal])
      .range([0, width]);

    const roundTo = 10000;
    let x = 0;
    const orderedColorData = colors.map((color, index) => {
      const group = colorLegendData.find(group => group.key === color);
      if (group) {
        group.x = x;
        group.width = Math.max(80, xScale(group.values.filter(v => v.height).length));
        // group.width = xScale(group.values.length);
        // group.width = xScale(group.values.map(v => v.values.length).reduce((a, v) => a + v, 0));
        x += group.width;
        const [start, end] = colorScale.invertExtent(color);
        group.start = start || 0;
        group.end = end || 0;
        const roundedValue = Math.round(group.start / roundTo) * roundTo;
        const displayValue = index === 0 ? group.start : roundedValue;
        group.label = `$${displayValue.toLocaleString()}${index === colors.length - 1 ? ' +' : ''}`;
        group.fontSize = fontScale(group.end);
      }
      return group;
    }).filter(g => g);

    return (
      <g transform={`translate(0, ${barWidth})`}>
        {orderedColorData.map((group, index) => (
          <Spring
            key={group.key}
            native
            from={{
              transform: `translate(0, 0)`,
              width: 0,
              // fontSize: fontScale.range()[0],
            }}
            to={{
              transform: `translate(${group.x}, 0)`,
              width: group.width,
              // fontSize: group.fontSize,
            }}
          >
            {({ transform, width, fontSize }) => (
              <animated.g
                key={group.key}
                transform={transform}
              >
                <animated.rect
                  width={width}
                  height={barWidth}
                  fill={group.key}
                />
                {group.x === 0 ? null : (
                  <line
                    stroke='white'
                    strokeWidth='2'
                    y2={barWidth}
                  />
                )}
                <animated.text
                  transform={`translate(0, ${-barWidth / 4})`}
                  fill={group.key}
                  // fontSize={fontSize}
                  fontSize={group.fontSize}
                  fontWeight='bold'
                >
                  {group.label}
                </animated.text>
              </animated.g>
            )}
          </Spring>
        ))}
      </g>
    );
  }, [width, colorLegendData]);

  return (
    <div className='Sankey'>
      <div className='instructions'>
        <div className='instruction'>
          <p>
            {localize(language, 'visualization', 'instruction')}
          </p>
        </div>
      </div>
      <div className='columnHeadings'>
        {columnToggles}
      </div>
      <div className='instructions' style={{ marginTop: '0.5em' }}>
        <div className='instruction'>
          <p
            style={{
              opacity: columnData.length && height ? 1 : 0,
            }}
          >
            {localize(language, 'visualization', 'legend description')}
          </p>
        </div>
        {
          // <div className='labelToggle'
          //   // style={{ opacity: 0.25, pointerEvents: 'none' }}
          //   >
          //   <input
          //     type='checkbox'
          //     id='inlineEverything'
          //     name='inlineEverything'
          //     checked={inlineEverything && !hideEverything}
          //     onChange={() => {
          //       setInlineEverything(inlineEverything => !inlineEverything)
          //       setHideEverything(hideEverything => !hideEverything)
          //     }}
          //   />
          //   <label htmlFor='inlineEverything'>
          //     Expanded View
          //   </label>
          //   <img
          //     src={info}
          //     alt='(i)'
          //     onMouseMove={e => {
          //       const { pageX, pageY } = e;
          //       setInstruction({
          //         x: pageX,
          //         y: pageY,
          //         copy: 'Always Show Unselected Categories'
          //         // ...dataset,
          //       });
          //     }}
          //     onMouseOut={() => {
          //       setInstruction(null);
          //     }}
          //     onClick={() => {
          //       setInlineEverything(inlineEverything => !inlineEverything)
          //       setHideEverything(hideEverything => !hideEverything)
          //     }}
          //   />
          // </div>
          // <div className='labelToggle'
          //   style={{ background: '#edc' }}
          //   >
          //   <input
          //     type='checkbox'
          //     id='filterBySelection'
          //     name='filterBySelection'
          //     checked={filterBySelection}
          //     onChange={() => {
          //       setFilterBySelection(filterBySelection => !filterBySelection)
          //     }}
          //   />
          //   <label htmlFor='filterBySelection'>
          //     TEMP: Filter By Selection (hover when not selected + not Advanced)
          //   </label>
          // </div>
        }
        {
          // <>
          //   <div className='labelToggle'>
          //     <input
          //       type='checkbox'
          //       id='inlineEverything'
          //       name='inlineEverything'
          //       // disabled={!selected}
          //       checked={inlineEverything}
          //       onChange={() => setInlineEverything(inlineEverything => !inlineEverything)}
          //     />
          //     <label htmlFor='inlineEverything'
          //       // style={{ opacity: !selected ? 0.5 : 1 }}
          //       >
          //       Show Unselected Categories
          //     </label>
          //   </div>
          //   <div className='labelToggle' style={{ marginLeft: '1em', background: 'rgba(255, 100, 0, 0.1)' }}>
          //     <input
          //       type='checkbox'
          //       id='hideEverything'
          //       name='hideEverything'
          //       // disabled={!selected}
          //       checked={hideEverything}
          //       onChange={() => setHideEverything(hideEverything => !hideEverything)}
          //     />
          //     <label htmlFor='hideEverything'>
          //       Hide all unselected categories
          //     </label>
          //   </div>
          //   <div className='labelToggle' style={{ marginLeft: '1em', background: 'rgba(255, 100, 0, 0.1)' }}>
          //     <input
          //       type='checkbox'
          //       id='hideOtherCols'
          //       name='hideOtherCols'
          //       // disabled={!selected}
          //       checked={hideOtherCols}
          //       onChange={() => setHideOtherCols(hideOtherCols => !hideOtherCols)}
          //     />
          //     <label htmlFor='hideOtherCols'>
          //       Hide categories in unselected columns
          //     </label>
          //   </div>
          // </>
        }
      </div>
      <div
        className='visual'
        style={{ height: `${barWidth * 2.5}px` }}
      >
        <svg
          className='svg'
          style={{ width, height: barWidth * 2.5, fontFamily: 'Open Sans' }}
        >
          {colorLegend}
        </svg>
      </div>
      <div
        ref={scrollRef}
        className='filterIndicator'
      >
        <div className={`indicator default${selectedDatum || !columns.length ? ' hidden' : ''}`}>
          <div className='filter'>
            {language === 'english' ?
              <span>Viewing <span className='emphasis'>All</span> Budget Items</span>
              : <span>Visualización de <span className='emphasis'>todos</span> los elementos del presupuesto</span>
            }
          </div>
        </div>
        <div
          className={`indicator selected${!selectedDatum ? ' hidden' : ''}`}
          style={selectedDatum ? selectedDatum.indicatorStyle : {}}
        >
          <div
            className='filter'
            style={selectedDatum ? selectedDatum.filterStyle : {}}
          >
            <div className='label'>
              {localize(language, 'visualization', 'filtered by').replace('[category]','')}

              <span className='emphasis' style={{ color: selectedDatum ? selectedDatum.color : null }}>{selectedDatum ? selectedDatum.name : ''}</span>
            </div>
            <div
              className='close'
              onClick={() => {setSelected(null)}}
            >
              <span>{localize(language, 'visualization', 'clear filter')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className='columnHeadings'>
        {columnSelects}
      </div>
      {
        instruction ? (
          <div
            className='segmentInstruction'
            style={{
              marginLeft: instruction.x,
              marginTop: instruction.y,
              transform: instruction.x > width / 2
                ? `translate(-110%, -50%)`
                : `translate(10%, -50%)`,
            }}
          >
            <span>
              {
                instruction.copy
                  ? instruction.copy
                  : localize(language, 'visualization', 'tooltip click instruction 3')
              }
            </span>
          </div>
        ) : null
      }
      {
        tooltip ? (
          <div
            className='tooltip'
            style={{
              marginLeft: tooltip.x,
              marginTop: tooltip.y,
              // borderColor: tooltip.color,
              // transform: `translate(${tooltip.x > window.innerWidth / 3 * 2 ? '-105%' : '5%'}, -50%)`,
              transform: `translate(${tooltip.h === 'right' ? '5%' : '-105%'}, ${tooltip.v > height / 4 * 3 ? '-105%' : '5%' })`,
            }}
            onClick={e => e.stopPropagation()}
            onTouchEnd={e => e.stopPropagation()}
            onTouchStart={e => {
              e.stopPropagation();
              setTooltip(null);
            }}
          >
            <div className='heading' style={{ color: tooltip.color }}>
              <div className='row name'>
                <span>
                  {tooltip.parent}
                </span>
              </div>
              <div className='row name'>
                <svg width='22' height='17' viewBox='0 0 22 17'>
                  <g transform='translate(-734 -460)'>
                    <rect width='22' height='17' transform='translate(734 460)' fill='none'/>
                    <line y2='8' transform='translate(741.5 462.5)' fill='none' stroke={tooltip.color} strokeLinecap='round' strokeWidth='1'/>
                    <line x1='10' transform='translate(741.5 470.5)' fill='none' stroke={tooltip.color} strokeLinecap='round' strokeWidth='1'/>
                  </g>
                </svg>
                <span>
                  {tooltip.name}
                </span>
              </div>
            </div>
            <div className='content'>
              <div className='row'>
                <span>
                  {localize(language, 'visualization', 'tooltip description').replace('[sub category]', tooltip.name).replace('[category]', tooltip.parent)}
                </span>
              </div>
              <div className='row'>
                <div className='label' style={{ color: tooltip.color }}>
                  {localize(language, 'visualization', 'total cost')}
                </div>
                <div className='value'>
                  {tooltip.value}
                </div>
              </div>
              <div className='row'>
                <div className='label' style={{ color: tooltip.color }}>
                  {localize(language, 'visualization', 'cost per student')}
                </div>
                <div className='value'>
                  {tooltip.perstudent}
                </div>
              </div>
              <div className='row'>
                <div className='label' style={{ color: tooltip.color }}>
                  {localize(language, 'visualization', 'students served')}
                </div>
                <div className='value'>
                  {tooltip.students}
                </div>
              </div>
              <div className='row'>
                <span>
                  {
                    tooltip.type === 'open'
                      ? localize(language, 'visualization', 'tooltip click instruction')
                      : localize(language, 'visualization', 'tooltip click instruction 2')
                  }
                </span>
              </div>
            </div>
          </div>
        ) : null
      }
      <div
        className='visual'
        style={
          columnData.length && height ? {
            height: `${height}px`,
          } : null
        }
      >
        {hint}
        <svg
          className='svg'
          style={{ width, height, fontFamily: 'Open Sans' }}
          onContextMenu={downloadSVG}
          onMouseMove={e => {
            const { pageX, pageY, target } = e;
            const { dataset } = target;
            if (Object.keys(dataset).length) {
              const { type } = dataset;
              if (type === 'hoverhighlight') {
                const { hoverhighlight } = dataset;
                setHoverHighlight(hoverhighlight);
                setSubselect(null);
                setTooltip(null);
                setInstruction(null);
              } else if (type === 'subselect') {
                const { subselect } = dataset;
                setSubselect(subselect);
                setInstruction(null);
                setTooltip(null);
                setHoverHighlight(null);
              } else if (type === 'none') {
                // setSubselect(null);
                setInstruction(null);
                setTooltip(null);
                setHoverHighlight(null);
              } else if (type === 'hidden') {
                setSubselect(null);
                setTooltip(null);
                setHoverHighlight(null);
                setInstruction({
                  x: pageX,
                  y: pageY,
                  ...dataset,
                });
              } else {
                // setSubselect(null);
                setInstruction(null);
                setHoverHighlight(null);
                setTooltip({
                  x: pageX,
                  y: pageY,
                  ...dataset,
                });
              }
            } else {
              // setSubselect(null);
              setInstruction(null);
              setTooltip(null);
              setHoverHighlight(null);
            }
            // simplify this logic ^^^
          }}
          onMouseOut={() => {
            setTooltip(null);
            setInstruction(null);
            setHoverHighlight(null);
          }}
        >
          <defs>
            {chordGradients}
          </defs>
          <rect
            className='background'
            width={width}
            height={height}
            onClick={() => {
              setInstruction(null);
              setHoverHighlight(null);
              setTooltip(null);
              setSelected(null);
              setSubselect(null);
              clearHint();
            }}
            fill='white'
          />
          {chordPaths}
          {columnGroups}
        </svg>
      </div>
    </div>
  );
}
